import { CONFIG } from '../config-global';
import { Item } from '../types';

export type PrintMode = 'single-a6' | 'double-a6-separate' | 'double-a5';

export interface PrintReceiptData {
  customerName: string;
  customerPhone: string;
  customerAddress: string;
  items: Item[];
  totalAmount: number;
  date: string;
  purchaseNumber?: string;
  printMode?: PrintMode;
}

export const printReceipt = (data: PrintReceiptData) => {
  const printMode = data.printMode || 'double-a5';

  const formatCurrency = (value: number) => value.toLocaleString('vi-VN');
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('vi-VN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const numberToWords = (num: number): string => {
    if (num === 0) return 'không';
    
    const ones = ['', 'một', 'hai', 'ba', 'bốn', 'năm', 'sáu', 'bảy', 'tám', 'chín'];
    const tens = ['', '', 'hai mươi', 'ba mươi', 'bốn mươi', 'năm mươi', 'sáu mươi', 'bảy mươi', 'tám mươi', 'chín mươi'];
    
    const readGroup = (n: number): string => {
      let result = '';
      const hundred = Math.floor(n / 100);
      const ten = Math.floor((n % 100) / 10);
      const one = n % 10;
      
      if (hundred > 0) {
        result += ones[hundred] + ' trăm';
        if (ten === 0 && one > 0) result += ' lẻ';
      }
      
      if (ten > 1) {
        result += (result ? ' ' : '') + tens[ten];
        if (one === 1) {
          result += ' mốt';
        } else if (one === 5 && ten > 0) {
          result += ' lăm';
        } else if (one > 0) {
          result += ' ' + ones[one];
        }
      } else if (ten === 1) {
        result += (result ? ' ' : '') + 'mười';
        if (one === 5) {
          result += ' lăm';
        } else if (one > 0) {
          result += ' ' + ones[one];
        }
      } else if (one > 0) {
        result += (result ? ' ' : '') + ones[one];
      }
      
      return result;
    };
    
    if (num < 0) return 'âm ' + numberToWords(-num);
    if (num < 1000) return readGroup(num);
    
    const billion = Math.floor(num / 1000000000);
    const million = Math.floor((num % 1000000000) / 1000000);
    const thousand = Math.floor((num % 1000000) / 1000);
    const remainder = num % 1000;
    
    let result = '';
    
    if (billion > 0) {
      result += readGroup(billion) + ' tỷ';
    }
    
    if (million > 0) {
      result += (result ? ' ' : '') + readGroup(million) + ' triệu';
    }
    
    if (thousand > 0) {
      result += (result ? ' ' : '') + readGroup(thousand) + ' nghìn';
    }
    
    if (remainder > 0) {
      result += (result ? ' ' : '') + readGroup(remainder);
    }
    
    return result.trim();
  };

  const renderReceipt = () => `
    <div class="receipt">
      <div class="header">
        <div class="header-left">
          <div class="business-name">${CONFIG.businessName}</div>
          <div class="business-line">ĐC: ${CONFIG.businessAddress}</div>
          <div class="business-line">ĐT: ${CONFIG.businessPhone}</div>
        </div>
        <div class="header-right">
          <div class="receipt-title">PHIẾU THU MUA</div>
          <div class="receipt-line">📅 Ngày giờ: ${formatDate(data.date)}</div>
          <div class="receipt-line">🧾 Số phiếu: <strong style="font-weight: bold;font-size: 14px;">${data.purchaseNumber ?? '—'}</strong></div>
        </div>
      </div>
      <hr class="header-divider" />
      
      <div class="info-section">
        <div class="info-row">
          <div class="info-cell">
            <span class="info-label">Tên KH:</span>
            <span class="info-value">${data.customerName}</span>
          </div>
          <div class="info-cell">
            <span class="info-label">Điện thoại:</span>
            <span class="info-value">${data.customerPhone}</span>
          </div>
        </div>
        <div class="info-row">
          <div class="info-cell span-2">
            <span class="info-label">Địa chỉ:</span>
            <span class="info-value">${data.customerAddress}</span>
          </div>
        </div>
      </div>
      
      <table class="items-table">
        <thead>
          <tr>
            <th style="width: 50px;">STT</th>
            <th style="">Loại</th>
            <th style="max-width: 80px;">SL<br>(kg)</th>
            <th style="width: 50px;">Giá</th>
            <th style="max-width: 80px;">Thành tiền (đ)</th>
          </tr>
        </thead>
        <tbody>
          ${[...data.items].sort((a, b) => a.fruitTypeId > b.fruitTypeId ? 1 : -1).map((item, index) => `
            <tr>
              <td class="center">${index + 1}</td>
              <td>${item.fruitTypeName}</td>
              <td class="center">${item.weight}</td>
              <td class="right">${formatCurrency(item.price)}</td>
              <td class="right">${formatCurrency(item.total)}</td>
            </tr>
          `).join('')}
          <tr class="total-row">
            <td colspan="4" class="right">TỔNG CỘNG:</td>
            <td class="right" style="font-size: 20px; font-weight: bold;">${formatCurrency(data.totalAmount)}</td>
          </tr>
        </tbody>
      </table>
      
      <div style="margin: 6px 0; padding: 5px; background-color: #f9f9f9; border: 1px solid #ddd; border-radius: 4px;">
        <strong>Tổng tiền (bằng chữ):</strong> <em style="text-transform: capitalize;">${numberToWords(data.totalAmount)} đồng chẵn</em>
      </div>
      
      <div class="footer">
        <div class="signature-box">
          <div class="title">Người mua hàng</div>
          <div class="name">(Ký và ghi rõ họ tên)</div>
        </div>
        <div class="signature-box">
          <div class="title">Người bán hàng</div>
          <div class="name">(Ký và ghi rõ họ tên)</div>
        </div>
      </div>
    </div>
  `;

  // Xác định số lượng bản in và cấu hình CSS dựa trên printMode
  const receiptCount = printMode === 'single-a6' ? 1 : 2;
  const containerStyle = printMode === 'double-a5' 
    ? 'display: flex; gap: 10px; justify-content: center; height: 100%;'
    : 'display: block;';
  const receiptStyle = printMode === 'double-a5'
    ? 'width: 48%; border: 2px solid #ffffff; padding: 20px;'
    : 'width: 100%; border: 2px solid #ffffff; padding: 20px; page-break-after: always;';
  const pageSize = printMode === 'double-a5' ? 'A5 landscape' : 'A6 portrait';

  const html = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Phiếu Thu Mua</title>
  <style>
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    
    body {
      font-family: 'Arial', sans-serif;
      padding: 20px;
      margin: 0;
      font-size: 12px;
      ${printMode === 'double-a5' ? 'height: 100vh;' : ''}
    }
    
    .container {
      ${containerStyle}
    }
    
    .receipt {
      ${receiptStyle}
    }
    
    .header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: 10px;
      padding-bottom: 8px;
    }
    
    .header-left {
      text-align: center;
      max-width: fit-content;
      flex: 1;
    }
    
    .header-right {
      text-align: left;
      
    }
    
    .business-name {
      font-size: 18px;
      margin-bottom: 4px;
      text-transform: uppercase;
      font-weight: bold;
    }
    
    .business-line {
      font-size: 12px;
      margin-bottom: 4px;
    }
    
    .receipt-title {
      font-size: 18px;
      font-weight: bold;
      margin-bottom: 6px;
    }
    
    .receipt-line {
      font-size: 11px;
      margin-bottom: 4px;
    }
    
    .header-divider {
      border: none;
      border-top: 1px solid #333;
      margin-bottom: 6px;
      width: 100%;
      height: 0;
    }
    
    .info-section {
      margin-bottom: 15px;
    }
    
    .info-row {
      display: grid;
      font-size: 11px;
      grid-template-columns: repeat(3, 1fr);
      gap: 8px;
      margin-bottom: 6px;
      line-height: 1.4;
    }

    .info-cell {
      align-items: center;
      gap: 4px;
      min-width: 0;
    }
    
    .info-cell.span-2 {
      grid-column: span 2;
    }
    
    .info-label {
      font-weight: bold;
    }
    
    .info-value {
      margin-left: 4px;
      margin-right: 20px;
    }
    
    .items-table {
      width: 100%;
      border-collapse: collapse;
      margin: 15px 0;
      font-size: 11px;
    }
    
    .items-table th,
    .items-table td {
      border: 1px solid #333;
      padding: 5px;
      text-align: left;
    }
    
    .items-table th {
      background-color: #f0f0f0;
      font-weight: bold;
      text-align: center;
      font-size: 11px;
    }
    
    .items-table td.center {
      text-align: center;
    }
    
    .items-table td.right {
      text-align: right;
    }
    
    .total-row {
      font-weight: bold;
      background-color: #f9f9f9;
    }
    
    .total-row td {
      border-top: 2px solid #333;
    }
    
    .footer {
      margin-top: 20px;
      display: flex;
      justify-content: space-between;
    }
    
    .signature-box {
      text-align: center;
      min-width: 120px;
      font-size: 11px;
    }
    
    .signature-box .title {
      font-weight: bold;
      margin-bottom: 40px;
    }
    
    .signature-box .name {
      border-top: 1px solid #333;
      padding-top: 5px;
      font-style: italic;
    }
    
    @media print {
      body {
        padding: 20px;
        margin: 0;
        height: 100vh;
      }
      
      .container {
        height: 100%;
        width: 100%;
        ${printMode === 'double-a5' ? 'gap: 0;' : ''}
      }
      
      .receipt {
        border: 2px solid #ffffff;
        ${printMode === 'double-a5' ? 'width: 50%; height: 100%;' : 'width: 100%; height: 100%;'}
        ${printMode === 'double-a6-separate' ? 'page-break-after: always;' : ''}
      }
      
      ${printMode === 'single-a6' ? '.receipt:last-child { page-break-after: avoid; }' : ''}
      
      @page {
        size: ${pageSize};
        margin: 0;
      }
    }
  </style>
</head>
<body>
  <div class="container">
    ${Array(receiptCount).fill(null).map(() => renderReceipt()).join('')}
  </div>
</body>
</html>
  `;

  // Kiểm tra xem có bật chế độ in tự động không
  const silentPrintEnabled = localStorage.getItem('silentPrintEnabled') === 'true';
  
  // Tạo iframe để in
  const iframe = document.createElement('iframe');
  iframe.style.position = 'absolute';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = 'none';
  iframe.style.visibility = 'hidden';
  document.body.appendChild(iframe);

  const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
  if (!iframeDoc) {
    alert('Không thể tạo iframe để in phiếu');
    return;
  }

  iframeDoc.write(html);
  iframeDoc.close();

  // In sau khi iframe load xong
  iframe.onload = () => {
    iframe.focus();
    
    if (silentPrintEnabled) {
      // In tự động - gửi trực tiếp đến máy in
      console.log('Silent print mode: In tự động đến máy in mặc định');
      iframe.contentWindow?.print();
      
      // Xóa iframe sau 2 giây để đảm bảo print job đã được gửi
      setTimeout(() => {
        if (iframe.parentNode) {
          document.body.removeChild(iframe);
        }
      }, 2000);
    } else {
      // In thường - hiện dialog chọn máy in
      iframe.contentWindow?.print();
      
      // Xóa iframe sau khi user đóng dialog (delay lâu hơn)
      setTimeout(() => {
        if (iframe.parentNode) {
          document.body.removeChild(iframe);
        }
      }, 5000);
    }
  };
};
